// import node module libraries
import React, { Fragment, useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Dropdown, Badge, Table, Row, Col, Card, ListGroup, Nav, Tab, Offcanvas, Button, Spinner, Image } from 'react-bootstrap';
import { MoreVertical, Edit, Trash } from 'react-feather';
import GKTippy from 'components/elements/tooltips/GKTippy';
import Moment from 'react-moment';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Import required custom components
import TanstackTable from 'components/elements/advance-table/TanstackTable';
import DotBadge from 'components/elements/bootstrap/DotBadge';
// Import Loading Animation
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

// import brand images
import DHLBadge from 'assets/images/brand/dhl-badge.png';
import DPDBadge from 'assets/images/brand/dpd-badge.png';

// Import the new components
import ModalDeleteFlow from './ModalDeleteFlow';

const ReturnsTable = ({ filterBy = '' }) => {
	// Define required states
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [showOffcanvas, setShowOffcanvas] = useState(false);
	const [selectedRow, setSelectedRow] = useState({});
	const [page, setPage] = useState(1);
	const [response, setData] = useState([]);

	const [isLoadingTable, setIsLoadingTable] = useState(true); // State for loading table
	const [isLoadingButton, setIsLoadingButton] = useState(false); // State for loading spinner
	const [isButtonDisabled, setIsButtonDisabled] = useState(false); // State for button disabled when loading

	const [isFailed, setIsFailed] = useState(null);// State for show errors
	const [isPassed, setIsPassed] = useState(null);// State for show passed message

	const auth = useAuthUser();
	const return_key = 'mention';

	// Methods / Functions
	const handleRowClick = (row, action) => {
		if (action === 'open') {
			setShowOffcanvas(true);
			fetchRowDetails(row); // Call API to fetch log details
		} else if (action === 'delete') {
			handleDeleteClick(row);
			setShowOffcanvas(false);
		}
	};

	// API-Call Fetch selected row Details for Modal
	const fetchRowDetails = async (rowID) => {
		try {
			// Dynamische URL basierend auf der Umgebung
			const baseURL = process.env.REACT_APP_ENV === 'development'
				? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
				: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

			const response = await axios.get(`${baseURL}/tenants/${auth.tenant_id}/returns/${return_key}/${rowID}`, options);
			const rowDetails = response.data;
			setSelectedRow(rowDetails.data); // Update selectedRow state with fetched row details
			setIsFailed(null);
		} catch (error) {
			const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
			handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');		
		}
	};

	// Delete Flow
	const [showDeleteModal, setShowDeleteModal] = useState(false); // State for showing modal
	const [flowToDelete, setFlowToDelete] = useState(null); // State for API Credential to delete
	const [isLoadingButtonDeleteFlow, setIsLoadingButtonDeleteFlow] = useState(null); // State for loading spinner

	const handleDeleteClick = (flow) => {
		setFlowToDelete(flow);
		setShowDeleteModal(true);
	};

	const handleConfirmDelete = async () => {
		if (flowToDelete) {
			setIsLoadingButtonDeleteFlow(true);
			const baseURL = process.env.REACT_APP_ENV === 'development'
				? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
				: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

			await axios.delete(`${baseURL}/tenants/${auth.tenant_id}/returns/${return_key}/${flowToDelete}`, options)
				.then(response => {
					setShowDeleteModal(false);
					setIsLoadingButtonDeleteFlow(false);
					handleShowNotification(setNotification, 'success', 'Erfolgreich', t(response.data.message));
					//setAPICredentials(apiCredentials.filter(credential => credential.id !== apiCredentialToDelete.id));
					// Refresh the table data after deletion
					getData(page); // Fetch the updated data
				})
				.catch((error) => {
					setShowDeleteModal(false);
					setIsLoadingButtonDeleteFlow(false);
					const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
					handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
				});
		}
	};
/*
	// API-Call to delete selected row 
	const deleteRow = async (rowID) => {
		setIsButtonDisabled(true);
		setIsLoadingButton(true); // Set loading to true

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		try {
			await axios.delete(`${baseURL}/tenants/${auth.tenant_id}/returns/${return_key}/${rowID}`, options);
			setIsFailed(null);
			// Refresh the table data after deletion
			await getData(page); // Fetch the updated data
		} catch (error) {
			const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
			handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');		
		}
	};*/

	// API-Call Options
	const authHeader = useAuthHeader();
	const options = {
		params: {
			page: page,
			limit: 100000
		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	// API-Call for all Data
	const getData = async (pageNumber)  => {
		setIsLoadingTable(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.get(`${baseURL}/tenants/${auth.tenant_id}/returns/${return_key}`, options)
			.then(response => {
				setData({data: response.data.data})
				setIsLoadingTable(false);
				setIsFailed(null);
			})
			.catch((error) => {
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
				setIsLoadingTable(false);		
			});
	}

	useEffect(() => {
		getData(page); // Fetch data for the current page
	}, [page]); // Fetch data whenever the page changes

	// Handle
	const handlePageChange = (newPage) => {
		setPage(newPage);
	};

	const handleCloseOffcanvas = () => setShowOffcanvas(false);

	const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
		<Link
			to="#"
			ref={ref}
			onClick={(e) => {
				e.preventDefault();
				onClick(e);
			}}
			className="btn-icon btn btn-ghost btn-sm rounded-circle"
		>
			{children}
		</Link>
	));

	// 
	// Components
	//

	// Component ActionMenu
	const ActionMenu = (row) => {
		return (
			<Dropdown>
				<Dropdown.Toggle as={CustomToggle}>
					<MoreVertical size="15px" className="text-secondary" />
				</Dropdown.Toggle>
				<Dropdown.Menu align="end">
					<Dropdown.Header>{t('ACTION')}</Dropdown.Header>
					<Dropdown.Item eventKey="1" onClick={() => handleRowClick(row.ID, 'open')}>
						<Edit size="14px" className="me-1" /> {t('Show')}
					</Dropdown.Item>
					<Dropdown.Item eventKey="2" onClick={() => handleRowClick(row.ID, 'delete')}>
						<Trash size="14px" className="me-1" /> {t('Delete')}
					</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown>
		);
	};

	// Component Tracking-Link
	const TrackingLink = (carrier, tracking_id) => {
		return <Link
			to={
				carrier.includes('DHL')
					? 'https://www.dhl.de/de/privatkunden/dhl-sendungsverfolgung.html?piececode=' + tracking_id
					: carrier.includes('DPD')
						? 'https://my.dpd.de/redirect.aspx?action=2&parcelno=' + tracking_id +'&locale=de_DE'
						: carrier.includes('XYZ')
							? 'https://haeppy.app'
							: '#'
			}
			target='_blank'
		>{tracking_id} 					
			<GKTippy content={carrier}>
				<i className="fe fe-external-link fs-4"></i>
			</GKTippy>
			</Link>
	}

	const TrackingBadge = (carrier) => {
		if (carrier.includes('DHL')) return <Image src={DHLBadge} alt={carrier} width={45} className="img-fluid" />
		if (carrier.includes('DPD')) return <Image src={DPDBadge} alt={carrier} width={45} className="img-fluid" />
	}

	// Component Return-Link
	const ReturnLink = (source, rma_id) => {
		return <Link
			to={
				source.includes('Amazon Marketplace')
					? 'https://sellercentral.amazon.de/returns/list?keyword=' + rma_id
					: source.includes('XYZ')
						? 'https://xyz/returns/' + rma_id + '&locale=de_DE'
						: '#'
			}
			target='_blank'
		>{rma_id} 
			<GKTippy content={source}>
				<i className="fe fe-external-link fs-4"></i>
			</GKTippy>
			</Link>
	}

	// Component Flow-Badge
	const haeppyBadge = (value) => {
		return <Badge className={`text-light-primary bg-primary`}>{value}</Badge>
	}

	// Component Status-Badge
	const statusBadge = (value) => {
		return (
			<Fragment>
				<DotBadge
					bg={
						value.includes('processing')
							? 'warning'
							: value === 'completed'
								? 'success'
								: value === 'new'
									? 'info'
									: value.includes('error')
										? 'danger'
										: 'info'
					}
				></DotBadge>
				{
					value.includes('processing')
						? t('Working')
						: value === 'completed'
							? t('Completed')
							: value === 'new'
								? t('New')
								: value.includes('error')
									? t('Error')
									: t('Unkown Status')
				}
			</Fragment>
		);
	}

	const columns = useMemo(
		() => [
			{
				accessorKey: 'return_status',
				header: t('Status'),
				cell: ({ getValue }) => {
					return statusBadge(getValue());
				}
			},
			{
				accessorKey: 'return_value.meta.source_datetime',
				header: t('Date'),
				cell: ({ getValue }) => {
					return <Moment utc={true} local={true} format="DD.MM.YY">{getValue()}</Moment>;
				}
			},
			{
				accessorKey: 'return_value.meta.source',
				header: t('Flow'),
				cell: ({ getValue }) => {
					return haeppyBadge(getValue() + ' -> ' + return_key.charAt(0).toUpperCase() + return_key.slice(1));
				}
			},
			{
				accessorKey: 'return_value.order_info.order_id',
				header: t('Order number'),
				cell: ({ getValue, row }) => {
					const rowId = row.original.id; // Adjust this based on your data structur
					return (
						<Link className="fw-semibold" onClick={(event) => {
							event.preventDefault();
							handleRowClick(rowId, 'open');
						}}>
							{getValue()} <i className="fe fe-arrow-right fs-4"></i>
						</Link>
					);
				}
			},
			{
				accessorKey: 'return_value.order_info.amazon_rma_id',
				header: t('RMA number'),
				cell: ({ getValue }) => {
					return ReturnLink('Amazon Marketplace', getValue());
				}
			},
			{
				accessorKey: 'return_value.order_parties.invoice_party.party.address.name',
				header: t('Buyer'),
				cell: ({ getValue }) => {
					return getValue();
				}
			},
			{
				accessorKey: 'return_value.order_item.tracking_id',
				header: t('Shipment'),
				cell: ({ row, getValue }) => (
					<Row>
						<div className="">
							<h5 className="mb-n1">{row.original.return_value.order_item.return_carrier !== ' ' ? TrackingLink(row.original.return_value.order_item.return_carrier, row.original.return_value.order_item.tracking_id) : '-'}</h5>
							<small>{t('Shipment number')}</small>
						</div>
						<div className="mt-2">
							<h5 className="mb-n1">{row.original.return_value.order_item.return_carrier !== ' ' ? TrackingBadge(row.original.return_value.order_item.return_carrier) : '-'}</h5>
							<small>{t('Forwarder')}</small>
						</div>
					</Row>
				),
			},
			{
				accessorKey: 'return_value.order_item.merchant_sku',
				header: t('Item'),
				cell: ({ row, getValue }) => (
					<Row>
						<div className="">
							<h5 className="mb-n1">{getValue()}</h5>
							<small>{t('SKU')}</small>
						</div>
						<div className="mt-2">
							<h5 className="mb-n1">{row.original.return_value.order_item.return_quantity}</h5>
							<small>{t('Quantity')}</small>
						</div>
					</Row>
				),
			},
			/*{
				accessorKey: 'return_value.order_item.return_quantity',
				header: 'Anzahl',
				cell: ({ getValue }) => {
					return getValue();
				}
			},*/
			{
				accessorKey: 'action',
				header: 'Aktion',
				cell: ({ row }) => {
					return <ActionMenu ID={row.original.id} />;
				}
			}
		],
		[]
	);

	const unfilteredData = useMemo(() => response?.data ?? [], [response.data]);

	// filtering....
	//const filter = useState(filterBy === '' ? response.data : response.data?.filter((item) => item.log_level === filterBy));
	const data = useMemo(() => unfilteredData ?? [], [unfilteredData]);

	if (isLoadingTable) {
		return (
			<Table className="text-nowrap">
				<thead >
					<tr>
						<th scope="col">{t('Status')}</th>
						<th scope="col">{t('Date')}</th>
						<th scope="col">{t('Flow')}</th>
						<th scope="col">{t('Order number')}</th>
						<th scope="col">{t('RMA number')}</th>
						<th scope="col">{t('Buyer')}</th>
						<th scope="col">{t('Shipment')}</th>
						<th scope="col">{t('Item')}</th>
						<th scope="col">{t('Action')}</th>
					</tr>
				</thead>
				<tbody>
					{Array(10).fill(0).map((_, index) => (
						<tr key={index}>
							<td><Skeleton /></td>
							<td><Skeleton /></td>
							<td><Skeleton /></td>
							<td><Skeleton /></td>
							<td><Skeleton /></td>
							<td><Skeleton /></td>
							<td><Skeleton count={2} /></td>
							<td><Skeleton count={2} /></td>
							<td><Skeleton /></td>
						</tr>
					))}
				</tbody>
			</Table>

		);
	}

	return (
		<div>
			<TanstackTable
				data={data}
				columns={columns}
				filter={true}
				filterPlaceholder="Suche"
				pagination={{
					currentPage: page,
					onPageChange: handlePageChange, // Pass the page change handler
				}}
				exportButton={false}
			/>	  
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			{selectedRow && Object.keys(selectedRow).length > 0 && (
				<Offcanvas show={showOffcanvas} onHide={handleCloseOffcanvas} placement="end">
					<Offcanvas.Header closeButton>
						<Offcanvas.Title><h2>{t('Details')}</h2></Offcanvas.Title>
					</Offcanvas.Header>
					<Offcanvas.Body>
						<Card className="mb-4">
							<Card.Header className="card-header-height d-flex justify-content-between align-items-center">
								<div>
									<h3 className="mb-0">{t('Marketplace Information')}</h3>
								</div>
								<div>
									<span>{haeppyBadge(selectedRow.return_value.meta.source)}</span>
								</div>
							</Card.Header>
							<Card.Body className="p-0">
								{/*  List group  */}
								<ListGroup variant="flush">
									<ListGroup.Item>
										<div className="mt-2">
											<div className="ms-2 mt-2">
												<h5 className="mb-n1">{selectedRow.return_value.order_info.order_id}</h5>
												<small>{t('Order number')}</small>
											</div>
											<div className="ms-2 mt-2">
												<h5 className="mb-n1">{ReturnLink(selectedRow.return_value.meta.source, selectedRow.return_value.order_info.amazon_rma_id)}</h5>
												<small>{t('RMA number')}</small>
											</div>
											<div className="ms-2 mt-2">
												<h5 className="mb-n1">{selectedRow.return_value.order_info.a_to_z_claim}</h5>
												<small>{t('A-to-Z Case')}</small>
											</div>
											<div className="ms-2 mt-2">
												<h5 className="mb-n1"><Moment utc={true} local={true} format="DD.MM.YY HH:mm [Uhr]">{selectedRow.return_value.meta.last_update}</Moment></h5>
												<small>{t('Last sync')}</small>
											</div>
										</div>
									</ListGroup.Item>
									<ListGroup.Item>
										<Tab.Container defaultActiveKey="return_details">
											<Nav className="nav-lb-tab">
												<Nav.Item>
													<Nav.Link eventKey="return_details" className="mb-sm-3 mb-md-0">
														{t('Return details')}
													</Nav.Link>
												</Nav.Item>
												<Nav.Item>
													<Nav.Link eventKey="return_details_invoice" className="mb-sm-3 mb-md-0">
														{t('Invoice details')}
													</Nav.Link>
												</Nav.Item>
											</Nav>
											<Tab.Content>
												<Tab.Pane eventKey="return_details" className="pb-4 p-4">
													<div className="mt-2">
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow?.return_value?.order_item?.asin ?? "-"}</h5>
															<small>{t('ASIN')}</small>
														</div>
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow?.return_value?.order_item?.merchant_sku ?? "-"}</h5>
															<small>{t('Own SKU')}</small>
														</div>
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow?.return_value?.order_item?.order_quantity ?? "-"}</h5>
															<small>{t('Order quantity')}</small>
														</div>
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow?.return_value?.order_item?.order_amount.toLocaleString() ?? "-"}  {selectedRow?.return_value?.order_item?.currency_code}</h5>
															<small>{t('Total amount')}</small>
														</div>
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow?.return_value?.order_item?.return_quantity ?? "-"}</h5>
															<small>{t('Quantity')}</small>
														</div>
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow?.return_value?.order_item?.return_reason ?? "-"}</h5>
															<small>{t('Reason for return')}</small>
														</div>
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow?.return_value?.order_item?.is_prime ?? "-"}</h5>
															<small>{t('Amazon Prime')}</small>
														</div>
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow && selectedRow.return_value && selectedRow.return_value.order_item && selectedRow.return_value.order_item.refunded_amount !== ' ' ? selectedRow.return_value.order_item.refunded_amount.toLocaleString() : "-"}</h5>
															<small>{t('Refund')}</small>
														</div>
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow && selectedRow.return_value && selectedRow.return_value.order_item && selectedRow.return_value.order_item.return_carrier !== ' ' ? TrackingBadge(selectedRow.return_value.order_item.return_carrier) : "-"}</h5>
															<small>{t('Forwarder')}</small>
														</div>
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow && selectedRow.return_value && selectedRow.return_value.order_item && selectedRow.return_value.order_item.tracking_id !== ' ' ? TrackingLink(selectedRow.return_value.order_item.return_carrier,selectedRow.return_value.order_item.tracking_id) : "-"}</h5>
															<small>{t('Shipment number')}</small>
														</div>
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow && selectedRow.return_value && selectedRow.return_value.order_item && selectedRow.return_value.order_item.label_type ? selectedRow.return_value.order_item.label_type : "-"}</h5>
															<small>{t('Label type')}</small>
														</div>
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow?.return_value?.order_item?.label_cost.toLocaleString() ?? "-"} {selectedRow?.return_value?.order_item?.currency_code}</h5>
															<small>{t('Label cost')}</small>
														</div>
													</div>
												</Tab.Pane>
												<Tab.Pane eventKey="return_details_invoice" className="pb-4 p-4 react-code">
													<div className="mt-2">
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow && selectedRow.return_value && selectedRow.return_value.order_info && selectedRow.return_value.order_info.invoice_id ? selectedRow.return_value.order_info.invoice_id : "-"}</h5>
															<small>{t('Own Invoice number')}</small>
														</div>
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow && selectedRow.return_value && selectedRow.return_value.order_parties && selectedRow.return_value.order_parties.buyer_party && selectedRow.return_value.order_parties.buyer_party.party_id ? selectedRow.return_value.order_parties.buyer_party.party_id : "-"}</h5>
															<small>{t('Own Customer number')}</small>
														</div>
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow && selectedRow.return_value && selectedRow.return_value.order_parties && selectedRow.return_value.order_parties.invoice_party && selectedRow.return_value.order_parties.invoice_party.party && selectedRow.return_value.order_parties.invoice_party.party.address && selectedRow.return_value.order_parties.invoice_party.party.address.name ? selectedRow.return_value.order_parties.invoice_party.party.address.name : "-"}</h5>
															<small>{t('Name')}</small>
														</div>
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow && selectedRow.return_value && selectedRow.return_value.order_parties && selectedRow.return_value.order_parties.invoice_party && selectedRow.return_value.order_parties.invoice_party.party && selectedRow.return_value.order_parties.invoice_party.party.address && selectedRow.return_value.order_parties.invoice_party.party.address.name3 ? selectedRow.return_value.order_parties.invoice_party.party.address.name3 : "-"}</h5>
															<small>{t('Addition')}</small>
														</div>
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow && selectedRow.return_value && selectedRow.return_value.order_parties && selectedRow.return_value.order_parties.invoice_party && selectedRow.return_value.order_parties.invoice_party.party && selectedRow.return_value.order_parties.invoice_party.party.address && selectedRow.return_value.order_parties.invoice_party.party.address.street ? selectedRow.return_value.order_parties.invoice_party.party.address.street : "-"}</h5>
															<small>{t('Street')}</small>
														</div>
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow && selectedRow.return_value && selectedRow.return_value.order_parties && selectedRow.return_value.order_parties.invoice_party && selectedRow.return_value.order_parties.invoice_party.party && selectedRow.return_value.order_parties.invoice_party.party.address && selectedRow.return_value.order_parties.invoice_party.party.address.zip ? selectedRow.return_value.order_parties.invoice_party.party.address.zip : "-"}</h5>
															<small>{t('Postal code')}</small>
														</div>
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow && selectedRow.return_value && selectedRow.return_value.order_parties && selectedRow.return_value.order_parties.invoice_party && selectedRow.return_value.order_parties.invoice_party.party && selectedRow.return_value.order_parties.invoice_party.party.address && selectedRow.return_value.order_parties.invoice_party.party.address.city ? selectedRow.return_value.order_parties.invoice_party.party.address.city : "-"}</h5>
															<small>{t('City')}</small>
														</div>
														<div className="ms-2 mt-2">
															<h5 className="mb-n1">{selectedRow && selectedRow.return_value && selectedRow.return_value.order_parties && selectedRow.return_value.order_parties.invoice_party && selectedRow.return_value.order_parties.invoice_party.party && selectedRow.return_value.order_parties.invoice_party.party.address && selectedRow.return_value.order_parties.invoice_party.party.address.country ? selectedRow.return_value.order_parties.invoice_party.party.address.country : "-"}</h5>
															<small>{t('Country')}</small>
														</div>
													</div>
												</Tab.Pane>
											</Tab.Content>
										</Tab.Container>
									</ListGroup.Item>
								</ListGroup>
							</Card.Body>
						</Card>
						<Card className="mb-4">
							{/* Card header */}
							<Card.Header className="card-header-height d-flex justify-content-between align-items-center">
								<div>
									{' '}
									<h4 className="mb-0">{t('Status')}</h4>
								</div>
								<div>
									<span>{statusBadge(selectedRow.return_status)}</span>
								</div>
							</Card.Header>

							{/* Card body */}
							<Card.Body className="">
								{/* List group */}
								<ListGroup variant="flush" className="list-timeline-activity">
									{selectedRow.return_value.meta.processing_steps.map((item, index) => {
										return (
											<ListGroup.Item className="px-0 pt-0 border-0 pb-6" key={index}>
												<Row className="position-relative">
													<Col xs="auto">
														<div className={`icon-shape icon-md border border-2 bg-white border-${item.status === 'success' ? 'success' : item.status === 'error' ? 'danger' : 'body'} text-primary rounded-circle`}>
															<i className={`fe fe-${item.status === 'success' ? 'check' : item.status === 'error' ? 'x' : 'clock'} text-${item.status === 'success' ? 'success' : item.status === 'error' ? 'danger' : 'body'}`}></i>
														</div>
													</Col>
													<Col className="ms-n3">
														<h4 className="mb-0 h5">{item.result === null ? item.id + '.'+ t('Step') : t(item.result)}</h4>
														<span className="text-muted fs-6">{item.timestamp && (<Moment utc={true} local={true} format="DD.MM HH:mm [Uhr]">{item.timestamp}</Moment>)}</span>
													</Col>
													<Col xs="auto">
														<span
															className={`mb-0 fw-bold text-${item.status === 'success' ? 'success' : item.status === 'error' ? 'danger' : 'body'}`}
														>
															{item.status === 'success' ? t('Successful') : item.status === 'error' ? t('Error') : t('Open')}
														</span>
													</Col>
												</Row>
											</ListGroup.Item>
										);
									})}
								</ListGroup>
							</Card.Body>
						</Card>
						<Row>
							<Col lg={12} md={12} sm={12}>
								<div className="pb-4 mb-2 d-md-flex align-items-center justify-content-between">
									<div>
										<Button variant="danger" disabled={isButtonDisabled} onClick={(event) => {
											event.preventDefault();
											handleRowClick(selectedRow.id, 'delete');
										}}>
											{isLoadingButton ? (
												<>
													<Spinner animation="border" size="sm" className="me-2" />
													{t('Delete Return')}
												</>
											) : (
													t('Delete Return')
											)}
										</Button>
									</div>
								</div>
							</Col>
						</Row>
					</Offcanvas.Body>
				</Offcanvas>
			)}
			{/* Delete Flow Modal */}
			<ModalDeleteFlow
				show={showDeleteModal}
				onHide={() => setShowDeleteModal(false)}
				handleConfirmDelete={handleConfirmDelete}
				isLoadingButton={isLoadingButtonDeleteFlow}
			/>
		</div>
		
	);
};

export default ReturnsTable;
